/**
 * A photo with the four corners of the receipt on it: drag a corner (finger or mouse) or move it
 * with the arrow keys. Used before the upload on the capture page and afterwards on the document
 * ("Zuschnitt anpassen"). `corners` null means the whole photo; its handles start at the photo's
 * corners, so a receipt whose edges were missed is cropped by dragging from there.
 */
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScanCorner, ScanPage } from "@/lib/api/documents";
import { CORNER_NAMES, moveCorner, nudge, pointerToPhoto, startCorners } from "@/lib/capture/crop";
import { cn } from "@/lib/utils/cn";

export function CropFrame({
  url,
  page,
  corners,
  onChange,
}: {
  /** The photo: its URL (a stored original), or the file itself (just taken). */
  url: string | Blob | undefined;
  page: ScanPage;
  corners: ScanCorner[] | null;
  onChange: (corners: ScanCorner[]) => void;
}) {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  // A file gets its own object URL, released once loaded (see Thumbnail in routes/capture.tsx).
  const showFile = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img || !url || typeof url === "string") return;
      const objectUrl = URL.createObjectURL(url);
      img.onload = () => URL.revokeObjectURL(objectUrl);
      img.src = objectUrl;
    },
    [url],
  );
  const shown = corners ?? startCorners({ ...page, corners: null });
  const at = (corner: ScanCorner) => ({
    left: `${(corner.x / page.sourceWidth) * 100}%`,
    top: `${(corner.y / page.sourceHeight) * 100}%`,
  });

  return (
    <div
      ref={frame}
      className="relative mx-auto max-h-[60vh] w-fit max-w-full touch-none select-none"
      style={{ aspectRatio: `${page.sourceWidth} / ${page.sourceHeight}` }}
    >
      {url && (
        <img
          ref={typeof url === "string" ? undefined : showFile}
          src={typeof url === "string" ? url : undefined}
          alt=""
          className="max-h-[60vh] w-auto max-w-full"
          draggable={false}
        />
      )}
      <svg
        className="pointer-events-none absolute inset-0 size-full"
        viewBox={`0 0 ${page.sourceWidth} ${page.sourceHeight}`}
        preserveAspectRatio="none"
      >
        <polygon
          points={shown.map((p) => `${p.x},${p.y}`).join(" ")}
          className={cn("fill-primary/15 stroke-primary", !corners && "opacity-40")}
          strokeWidth={Math.max(page.sourceWidth, page.sourceHeight) / 300}
        />
      </svg>
      {/* Handles even for the whole photo: a missed edge is corrected by dragging from there. */}
      {shown.map((corner, index) => (
        <button
          key={CORNER_NAMES[index]}
          type="button"
          aria-label={t(`crop.corners.${CORNER_NAMES[index]!}`)}
          data-testid={`crop-corner-${index}`}
          className="absolute size-7 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-primary bg-background/80 shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          style={at(corner)}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(index);
          }}
          onPointerMove={(event) => {
            if (dragging !== index || !frame.current) return;
            const rect = frame.current.getBoundingClientRect();
            onChange(
              moveCorner(
                shown,
                index,
                pointerToPhoto(event.clientX, event.clientY, rect, page),
                page,
              ),
            );
          }}
          onPointerUp={() => setDragging(null)}
          onKeyDown={(event) => {
            const moved = nudge(shown, index, event.key, event.shiftKey, page);
            if (!moved) return;
            event.preventDefault();
            onChange(moved);
          }}
        />
      ))}
    </div>
  );
}
